"use client";

import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { logjs } from "@lib/client";
import s from "./styles";
import { providers } from "@lib/client";
import { useFileupDialog } from "./useFileupDialog";

const { useWebAppContext } = providers;
const log = new logjs("FileupDialog");

function FileupDialog({ open, onClose, params }) {
  const { state, actions } = useWebAppContext();
  const [formData, formProps] = useFileupDialog(state, actions, params);
  const { fileData, file, error, IsLoading } = formData;
  const { isEditMode } = params || false;
  const { PartsName } = params;
  log.debug(`FileupDialog isEditMode:${isEditMode}`, formData);

  // 各枠ごとの file input 用 Ref
  const fileInputsRef = useRef({});
  const bulkFileInputRef = useRef(null);

  useEffect(() => {
    if (open) formProps.initParts();
  }, [open]);

  /***
   * ▼ 単一ファイルアップロード（まとめてなし）
   ***/
  const onChangeFile = async (fileList) => {
    if (!fileList || fileList.length === 0) return;

    const file = fileList[0];

    if (file.size > 4000000) {
      actions.showAlert("アップロードできるファイルサイズは4MBまでです。");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = String(e.target.result);

      formProps.setFile({
        url: dataUrl,
        originalname: file.name,
        canDelete: true,
      });

      await formProps.uploadFile({
        originalname: file.name,
        url: dataUrl,
      });
    };

    reader.readAsDataURL(file);
  };

  // ファイル選択トリガー（各枠）
  const triggerPick = (seq) => {
    const input = fileInputsRef.current[seq];
    if (input) input.click();
  };
  
  // 一括ファイル選択トリガー
  const triggerBulkPick = () => {
    if (bulkFileInputRef.current) bulkFileInputRef.current.click();
  };

  const handleDelete = async (seq) => {
    await formProps.deleteFile(seq);
  };
  const handleUploadFile = async (seq,dataUrl) => {
    await formProps.uploadFile({
      originalname: file.name,
      url: dataUrl,
    });
  };

  /***
   * ▼ グリッド表示（変更なし）
   ***/
  const renderFileGrid = () => {
    if (!Array.isArray(files)) return null;
    const maxSlots = 1;
    const FilesPerRow = 1;

    return Array.from({ length: Math.ceil(maxSlots / FilesPerRow) }).map((_, rowIdx) => (
      <s.FileGridRow key={rowIdx}>
        {Array.from({ length: FilesPerRow }).map((__, colIdx) => {
          const seq = rowIdx * FilesPerRow + colIdx + 1;
          if (seq > maxSlots) return <td key={colIdx} />;

          const slot = files[seq - 1];

          return (
            <s.FileCell key={colIdx}>
              <s.PartsMini>
                <s.FileDrop>
                  <img src={slot?.url || "/files/noimage.png"} alt="写真" />
                </s.FileDrop>

                {isEditMode && (
                  <s.MiniRightBox>
                    <s.FileMask2>
                      <s.HiddenFile
                        ref={(el) => {
                          if (!el) return;
                          fileInputsRef.current[String(seq)] = el;
                        }}
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={(e) => onChangeFile(e.target.files)}
                      />

                      <s.FileMask2Span
                        onClick={async () => {
                          if (slot?.canDelete) {
                            const _ret = await params.actions?.showConfirm?.(
                              "確認",
                              "現在登録されている写真に上書きされますがよろしいですか？"
                            );
                            if (!_ret) return;
                          }
                          triggerPick(seq);
                        }}
                      >
                        参照
                      </s.FileMask2Span>

                      <s.HiddenText type="text" />
                    </s.FileMask2>

                    {slot?.canDelete ? (
                      <s.BtnGray onClick={() => handleDelete(seq)} className="delete_btn">
                        削除
                      </s.BtnGray>
                    ) : (
                      <s.BtnGray className="delete_btn disable-btn">削除</s.BtnGray>
                    )}
                  </s.MiniRightBox>
                )}
              </s.PartsMini>
            </s.FileCell>
          );
        })}
      </s.FileGridRow>
    ));
  };
  
  const { request } = fileData || {};

  const partsName = fileData?.partsName?.P_PartsName;
  const othPartsName = fileData?.PartsAnsers?.OthPartsName || PartsName;
  const fileName = fileData?.partsFile?.DocPath;
  const imageUrl =
    fileData?.partsFile?.DocPath
      ? `${state?.initData?.S3ImageFolderDirect}/${fileData.partsFile.DocPath}?v=${Date.now()}`
      : null;
  const displayName = (file?.originalname || fileName || "未登録").split("/").pop();

  const isModal = false;
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      hideBackdrop={!isModal}
      style={!isModal ? { pointerEvents: "none" } : { pointerEvents: "auto" }}
      BackdropProps={
        !isModal
          ? {
              style: { pointerEvents: "none" }, // 背景は操作不可にする
            }
          : {}
      }
      PaperProps={
        !isModal
          ? {
              style: { pointerEvents: "auto" }, // ダイアログ自体は操作可能にする
            }
          : {}
      }
    >
      {/* 省略：タイトルや車輌情報テーブルは元のまま動作 */}
      <s.FileContainer>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "url(/images/bg.png) 0 0",
          }}
        >
          <s.FlexRow>
            <Typography
              variant="h5"
              component="div"
              sx={{
                background: "url(/images/window_title.png) no-repeat 0 50%",
                padding: "5px 0 0 35px",
                color: "#333",
                fontSize: "24px",
                lineHeight: "30px",
                marginBottom: "10px",
              }}
            >
              {isEditMode ? "ファイル登録" : "ファイル参照"}
            </Typography>
          </s.FlexRow>
          <IconButton onClick={onClose} size="small" 
            sx={{
              backgroundColor: '#999', 
              color: '#fff',           
              borderRadius: 2,          
              '&:hover': {
                backgroundColor: '#777'
              },
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
                <DialogContent
                  sx={{
                    p: 3,
                    background: "url(/images/bg.png) 0 0",
                    overflow: "visible !important",
                  }}
                >
        {/* 車輌情報 */}
        <s.DetailTableCustom>
          <tbody>
            <tr>
              <td>
                <span className="label">見積番号</span>
                <span className="value">{request?.MitsumoriNo || ""}</span>

                <span className="space" />

                <span className="label">車名</span>
                <span className="value">{request?.CarName || ""}</span>

                <span className="space" />

                <span className="label">品名</span>
                <span className="value">{othPartsName || partsName || ""}</span>
              </td>
            </tr>

            <tr>
              <td>
                <span className="label">車輌型式</span>
                <span className="value">{request?.Sharyo_Katashiki || ""}</span>

                <span className="space" />

                <span className="label">年式</span>
                <span className="value">{request?.Sharyo_Nenshiki || ""}</span>
              </td>
            </tr>
          </tbody>
        </s.DetailTableCustom>

        <s.NormalTable className="imgedit">
          <tbody>
            <tr>
              <td style={{ padding: "10px" }}>
                <s.PartsMini>
                  {/* ファイル名表示 */}
                  <div
                    style={{
                      marginBottom: 8,
                      textAlign: "center",
                      cursor: imageUrl ? "pointer" : "default",
                      color: imageUrl ? "#0070f3" : "#999",
                    }}
                    onClick={() => {
                      if (!imageUrl) return;
                      window.open(imageUrl, "_blank");
                    }}
                  >
                    {displayName}
                  </div>

                  {isEditMode && (
                    <s.MiniRightBox>
                      <s.FileMask2>
                        <s.HiddenFile
                          ref={bulkFileInputRef}
                          type="file"
                          accept="image/jpeg,image/png"
                          onChange={(e) => onChangeFile(e.target.files)}
                        />
                        <s.FileMask2Span
                          onClick={async () => {
                            if (file?.canDelete) {
                              const ret = await params.actions?.showConfirm?.(
                                "確認",
                                "現在登録されているファイルに上書きされますがよろしいですか？"
                              );
                              if (!ret) return;
                            }
                            bulkFileInputRef.current?.click();
                          }}
                        >
                          参照
                        </s.FileMask2Span>
                      </s.FileMask2>

                      {fileName ? (
                        <s.BtnGray onClick={formProps.deleteFile}>削除</s.BtnGray>
                      ) : (
                        <s.BtnGray className="disable-btn">削除</s.BtnGray>
                      )}
                    </s.MiniRightBox>
                  )}
                </s.PartsMini>
              </td>
            </tr>
          </tbody>
        </s.NormalTable>
        {error && <s.ErrorMessage>{error}</s.ErrorMessage>}
        {/* <s.NormalTable className="imgedit">
          <tbody>{renderFileGrid()}</tbody>
        </s.NormalTable> */}

        {isEditMode && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
            <s.BtnRed onClick={onClose}>▶▶ファイルを登録する</s.BtnRed>
          </Box>
        )}

        {IsLoading && (
          <s.LoadingOverlay>
            <s.LoadingSpinner />
          </s.LoadingOverlay>
        )}
      </DialogContent>
      </s.FileContainer>
    </Dialog>
  );
}

export default FileupDialog;
